import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Search, ShoppingBag, User, Menu, X, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SearchModal from './SearchModal';

const SUBNAV = [
  { to: '/', label: 'Home' },
  { to: '/shop', label: 'Prodotti' },
  { to: '/chi-siamo', label: 'Chi siamo' },
  { to: '/informative-generali', label: 'Informative generali' },
];

export default function Header() {
  const { count, setIsOpen } = useCart();
  const { vacationMode, returnDate } = useSiteSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [prodottiOpen, setProdottiOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      {vacationMode ? (
        <div data-testid="promo-bar-vacation" className="bg-amber-900 text-amber-50 text-xs md:text-sm tracking-wide overflow-hidden">
          <div className="py-2.5 flex whitespace-nowrap animate-marquee will-change-transform">
            {[0, 1].map(rep => (
              <div key={rep} className="flex items-center gap-6 pr-6 shrink-0" aria-hidden={rep === 1}>
                <span className="font-medium">🌿 Chiusura per ferie{returnDate ? ` · Riapriamo il ${returnDate}` : ''}</span><span className="opacity-40">·</span>
                <span>Ordini momentaneamente sospesi</span><span className="opacity-40">·</span>
                <span>Puoi comunque sfogliare i prodotti</span><span className="opacity-40">·</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="promo-bar" className="bg-essenya-ink text-white text-xs md:text-sm tracking-wide overflow-hidden">
          <div className="py-2.5 flex whitespace-nowrap animate-marquee will-change-transform">
            {[0, 1].map(rep => (
              <div key={rep} className="flex items-center gap-6 pr-6 shrink-0" aria-hidden={rep === 1}>
                <span>10% di sconto con la newsletter</span><span className="opacity-40">·</span>
                <span>Spedizione gratuita sopra 25€</span><span className="opacity-40">·</span>
                <span>Made in Italy</span><span className="opacity-40">·</span>
                <span>Reso facile entro 14 giorni</span><span className="opacity-40">·</span>
                <span>Pagamenti sicuri</span><span className="opacity-40">·</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <header data-testid="site-header" className="sticky top-0 z-40 liquid-glass-panel border-x-0 border-t-0">
        <div className="container-essenya flex items-center justify-between py-4 md:py-5">
          <button data-testid="mobile-menu-toggle" className="md:hidden p-2 -ml-2" onClick={() => setMobileOpen(true)} aria-label="Apri menu">
            <Menu size={22} strokeWidth={1.5} />
          </button>
          <Link to="/" data-testid="logo-link" className="font-display text-2xl md:text-3xl tracking-[0.18em] font-medium">ESSENYA</Link>
          <div className="flex items-center gap-1 md:gap-2">
            <button data-testid="search-toggle" onClick={() => setSearchOpen(true)} className="p-2 hover:opacity-70 transition" aria-label="Cerca">
              <Search size={20} strokeWidth={1.5} />
            </button>
            <button data-testid="account-link" onClick={() => navigate('/account')} className="p-2 hover:opacity-70 transition hidden md:inline-flex" aria-label="Account">
              <User size={20} strokeWidth={1.5} />
            </button>
            <button data-testid="cart-toggle" onClick={() => setIsOpen(true)} className="p-2 hover:opacity-70 transition relative" aria-label="Carrello">
              <ShoppingBag size={20} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-essenya-ink text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-medium">{count}</span>
              )}
            </button>
          </div>
        </div>

        {/* Sub-navigation under the logo */}
        <nav data-testid="header-subnav" className="hidden md:block border-t border-essenya-border/70">
          <div className="container-essenya flex items-center gap-8 py-3 text-sm tracking-wide">
            {SUBNAV.map(n => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`subnav-${n.to === '/' ? 'home' : n.to.replace('/', '')}`}
                className="text-essenya-ink/80 hover:text-essenya-ink transition-colors uppercase text-xs tracking-[0.18em] font-medium"
              >
                {n.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 ${mobileOpen ? '' : 'pointer-events-none'}`} data-testid="mobile-menu-drawer">
        <div className={`absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity ${mobileOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setMobileOpen(false)} />
        <aside className={`absolute top-0 left-0 h-full w-[85%] max-w-sm liquid-glass-panel border-y-0 border-l-0 rounded-r-[2rem] shadow-2xl flex flex-col transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="flex items-center justify-between p-5 border-b border-white/40">
            <span className="font-display text-xl tracking-[0.18em]">ESSENYA</span>
            <button onClick={() => setMobileOpen(false)} aria-label="Chiudi menu" data-testid="mobile-menu-close"><X size={22} strokeWidth={1.5} /></button>
          </div>
          <nav className="flex-1 overflow-y-auto p-5 space-y-4">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block text-lg font-display">Home</Link>

            {/* Prodotti accordion */}
            <div>
              <button
                data-testid="mobile-prodotti-toggle"
                onClick={() => setProdottiOpen(o => !o)}
                className="w-full flex items-center justify-between text-lg font-display"
              >
                Prodotti <ChevronDown size={18} strokeWidth={1.5} className={`transition-transform duration-300 ${prodottiOpen ? 'rotate-180' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${prodottiOpen ? 'max-h-72 mt-3' : 'max-h-0'}`}>
                <div className="pl-4 border-l border-white/50 space-y-3">
                  {[['/shop', 'Tutti i prodotti'], ['/lavapavimenti', 'Lavapavimenti'], ['/sgrassatori', 'Sgrassatori'], ['/bundle', 'Bundle']].map(([to, label]) => (
                    <Link key={to} to={to} onClick={() => setMobileOpen(false)} data-testid={`mobile-sub-${to.replace('/', '')}`} className="block text-base text-essenya-ink/85">{label}</Link>
                  ))}
                </div>
              </div>
            </div>

            <Link to="/come-si-usa" onClick={() => setMobileOpen(false)} className="block text-lg font-display">Come si usa</Link>
            <Link to="/chi-siamo" onClick={() => setMobileOpen(false)} className="block text-lg font-display">Chi siamo</Link>
            <Link to="/faq" onClick={() => setMobileOpen(false)} className="block text-lg font-display">FAQ</Link>
            <Link to="/contatti" onClick={() => setMobileOpen(false)} className="block text-lg font-display">Contatti</Link>
            <div className="pt-3 border-t border-white/40 space-y-4">
              <Link to="/account" onClick={() => setMobileOpen(false)} className="block text-base text-essenya-text">Account</Link>
              <Link to="/informative-generali" onClick={() => setMobileOpen(false)} className="block text-base text-essenya-text" data-testid="mobile-informative-link">Informative generali</Link>
              <Link to="/tracking" onClick={() => setMobileOpen(false)} className="block text-base text-essenya-text">Traccia ordine</Link>
            </div>
          </nav>
        </aside>
      </div>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
